import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  SECTION_ICONS,
  sectionFieldApplies,
  type HomeLayoutProblem,
  type HomeSectionCatalog,
  type HomeSectionField,
  type HomeSectionItems,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { categorySlugOptions, productSlugOptions } from '@/lib/options';
import { CORE_SECTIONS, ICON_COPY, fieldCopy, itemCopy, linkKindOf, optionLabel, problemMessage, type LinkKind } from '@/lib/home-sections';
import { moveItem } from '@/lib/description-blocks';
import { AsyncSelect } from '@/components/async-select';
import { BilingualField, SelectField, TextField, ToggleField, readBilingual, readString, writeBilingual } from '@/components/fields';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  CopyIcon,
  PlusIcon,
  SectionGlyph,
  TrashIcon,
  UploadIcon,
} from '@/components/icons';
import { Segmented } from '@/components/page';
import { ProductPicker } from '@/components/product-picker';
import { Button, IconButton, Input, Select, cx } from '@/components/ui';

/**
 * A section's form, drawn from its description in the catalog (ADR-056).
 *
 * Nothing here knows what a slideshow is: it knows what a field of each kind
 * looks like, and the catalog says which fields a slideshow has. A field that
 * only applies to one choice — a rail's category, while its products come from
 * one — is asked for only then, and what was typed into it is kept, so
 * switching the source back and forth loses nothing.
 */

/** What every field may need to know about the store around it. */
export interface EditorContextValue {
  /** The store's content pages, for a link to one. */
  pages: { slug: string; label: string }[];
  /** Category names by slug; null until loaded. */
  categoryNames: Map<string, string> | null;
  /** The core's sections with the theme's merged in (ADR-057). */
  catalog: HomeSectionCatalog;
}

export const EditorContext = createContext<EditorContextValue>({ pages: [], categoryNames: null, catalog: CORE_SECTIONS });

/** Fields that read best at half the width, beside another. */
const NARROW = new Set(['number', 'select', 'category']);

export function SectionFields({
  type,
  fields,
  config,
  onChange,
  problems,
}: {
  type: string;
  fields: readonly HomeSectionField[];
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** This config's own problems — a section's, or one item's. */
  problems: HomeLayoutProblem[];
}) {
  const set = (name: string, value: unknown) => {
    const next = { ...config };
    if (value === undefined) delete next[name];
    else next[name] = value;
    onChange(next);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields
        .filter((field) => sectionFieldApplies(field, config, fields))
        .map((field) => {
          const wide = !NARROW.has(field.kind) || (field.kind === 'select' && (field.options?.length ?? 0) > 3);
          const own = problems.filter((problem) => problem.field === field.name);
          return (
            <div key={field.name} className={cx(wide && 'sm:col-span-2')}>
              <FieldControl type={type} field={field} value={config[field.name]} onChange={(value) => set(field.name, value)} />
              {own.map((problem, index) => (
                <p key={index} role="alert" className="mt-1 text-xs text-danger">
                  <ProblemText problem={{ ...problem, item: undefined }} />
                </p>
              ))}
            </div>
          );
        })}
    </div>
  );
}

function ProblemText({ problem }: { problem: HomeLayoutProblem }) {
  const locale = useUiStore((state) => state.locale);
  const { catalog } = useContext(EditorContext);
  return <>{problemMessage(problem, locale, catalog)}</>;
}

function FieldControl({
  type,
  field,
  value,
  onChange,
}: {
  type: string;
  field: HomeSectionField;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const copy = fieldCopy(type, field.name, field);
  const label = copy.label[locale];
  const hint = copy.hint?.[locale];

  switch (field.kind) {
    case 'bilingual':
    case 'bilingualText':
      return (
        <BilingualField
          label={label}
          hint={hint}
          limit={field.max}
          multiline={field.kind === 'bilingualText'}
          rows={5}
          placeholders={field.placeholder}
          value={readBilingual(value)}
          // Kept as typed; the API trims on save, and trimming here would eat a
          // space the moment it is typed.
          onChange={(next) => onChange(next.ar || next.en ? next : writeBilingual(next))}
        />
      );
    case 'number':
      return (
        <TextField
          label={label}
          hint={hint ?? (field.min !== undefined && field.max !== undefined ? t('numberRange', { min: count(field.min, locale), max: count(field.max, locale) }) : undefined)}
          latin
          inputMode="numeric"
          placeholder={field.default !== undefined ? String(field.default) : undefined}
          value={readString(value)}
          onChange={(next) => onChange(next.trim() === '' ? undefined : Number(next.replace(/[^\d]/g, '')))}
        />
      );
    case 'toggle':
      return <ToggleField label={label} hint={hint} checked={value === undefined ? field.default === true : value === true} onChange={onChange} />;
    case 'select': {
      const current = typeof value === 'string' ? value : String(field.default ?? field.options?.[0] ?? '');
      const options = (field.options ?? []).map((option) => ({ id: option, value: option, label: optionLabel(field.name, option, locale, field) }));
      // A few short choices read at a glance side by side; longer ones need a list.
      if (options.length <= 3 && options.every((option) => option.label.length <= 12)) {
        return (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">{label}</span>
            <div>
              <Segmented label={label} items={options} value={current} onChange={onChange} />
            </div>
            {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
          </div>
        );
      }
      return <SelectField label={label} hint={hint} value={current} options={options} onChange={onChange} />;
    }
    case 'image':
    case 'video':
      return (
        <MediaField
          kind={field.kind}
          label={label}
          hint={hint}
          value={value}
          onChange={onChange}
          portrait={field.name.toLowerCase().includes('mobile')}
        />
      );
    case 'link':
      return <LinkField label={label} hint={hint} value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    case 'category':
      return <CategoryField label={label} hint={hint} value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    case 'categories':
      return (
        <CategoriesField
          label={label}
          hint={hint}
          max={field.max ?? 24}
          value={Array.isArray(value) ? value.filter((slug): slug is string => typeof slug === 'string') : []}
          onChange={(next) => onChange(next.length > 0 ? next : undefined)}
        />
      );
    case 'products':
      return (
        <ProductPicker
          ordered
          label={label}
          hint={hint}
          selectedIds={Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []}
          onChange={(next) => onChange(next.length > 0 ? next.slice(0, field.max ?? 48) : undefined)}
        />
      );
    case 'icon':
      return <IconField label={label} value={typeof value === 'string' ? value : String(field.default ?? '')} onChange={onChange} />;
    default:
      return null;
  }
}

// ─── Images ──────────────────────────────────────────────────────────────────

/** What the editor shows for an image value: an uploaded `{ key, url }`, or a path written before uploads. */
function imageSource(value: unknown): string | undefined {
  if (typeof value === 'string') return mediaUrl(value);
  if (value && typeof value === 'object') {
    const url = (value as { url?: unknown }).url;
    if (typeof url === 'string') return mediaUrl(url);
  }
  return undefined;
}

/** A photograph or a film (ADR-057): uploaded, stored as its key, shown back as it will be seen. A page's share picture uses it too (ADR-058). */
export function MediaField({
  kind,
  label,
  hint,
  value,
  onChange,
  portrait,
}: {
  kind: 'image' | 'video';
  label: string;
  hint?: string;
  value: unknown;
  onChange: (next: unknown) => void;
  /** An upright frame, for a phone's picture. */
  portrait: boolean;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const src = imageSource(value);
  const film = kind === 'video';

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const result = await withSession(() => (film ? adminApi.uploads.video(file) : adminApi.uploads.image(file)));
      // The key is what is stored (ADR-013); the url is only for this preview.
      onChange({ key: result.key, url: result.url });
    } catch (cause) {
      setError(errorMessage(cause, t('previewFailed')));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={uploading}
          aria-label={src ? `${t('replaceImage')} — ${label}` : `${t('uploadImage')} — ${label}`}
          className={cx(
            'relative shrink-0 overflow-hidden rounded-control border border-dashed border-line bg-surface-sunken transition-colors hover:border-brand-300',
            portrait ? 'aspect-[3/4] w-20' : 'aspect-video w-36',
          )}
        >
          {src && film ? (
            <video src={src} muted playsInline preload="metadata" className="absolute inset-0 size-full object-cover" />
          ) : src ? (
            <img src={src} alt="" className="absolute inset-0 size-full object-cover" />
          ) : (
            <span className="absolute inset-0 flex items-center justify-center text-ink-faint">
              <UploadIcon className="size-5" />
            </span>
          )}
        </button>
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" disabled={uploading} onClick={() => input.current?.click()}>
              <UploadIcon className="size-4" />
              {uploading ? t('uploadingImage') : src ? t('replaceImage') : t('uploadImage')}
            </Button>
            {src ? (
              <Button variant="ghost" size="sm" disabled={uploading} onClick={() => onChange(undefined)}>
                {t('removeImage')}
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-ink-muted">{hint ?? t(film ? 'videoUploadHint' : 'imageUploadHint')}</p>
          {error ? <p role="alert" className="text-xs text-danger">{error}</p> : null}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept={film ? 'video/mp4,video/webm' : 'image/jpeg,image/png,image/webp,image/avif'}
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

// ─── Links ───────────────────────────────────────────────────────────────────

const LINK_KINDS: readonly LinkKind[] = ['none', 'category', 'product', 'page', 'products', 'custom'];

const PATHS: Record<'category' | 'product' | 'page', string> = { category: 'categories', product: 'products', page: 'pages' };

/**
 * A link chosen from what the store has — a category, a product, a page — and
 * stored as the path a shopper follows, like the header menu. Anything else
 * is typed: a filtered listing, or another site.
 */
function LinkField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (next: unknown) => void }) {
  const t = useT();
  const id = useId();
  const { pages } = useContext(EditorContext);
  const parsed = linkKindOf(value);
  // Held apart from the value: "a category" with none chosen yet is a choice
  // the stored path cannot express.
  const [kind, setKind] = useState<LinkKind>(parsed.kind);
  useEffect(() => {
    if (value) setKind(linkKindOf(value).kind);
  }, [value]);

  const choose = (next: LinkKind) => {
    setKind(next);
    if (next === 'none') onChange(undefined);
    else if (next === 'products') onChange('/products');
    else if (next !== 'custom') onChange(undefined);
  };
  const to = (target: string) => (target && kind !== 'none' && kind !== 'custom' && kind !== 'products' ? `/${PATHS[kind]}/${target}` : undefined);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`${id}-kind`} className="text-sm font-medium text-ink">
        {label}
      </label>
      <div className="grid gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
        <Select id={`${id}-kind`} value={kind} onChange={(event) => choose(event.target.value as LinkKind)}>
          {LINK_KINDS.map((option) => (
            <option key={option} value={option}>
              {option === 'none' ? t('linkNone') : t(`linkKind_${option}`)}
            </option>
          ))}
        </Select>
        {kind === 'category' ? (
          <CategorySelect id={`${id}-target`} value={parsed.kind === 'category' ? parsed.target : ''} onChange={(slug) => onChange(to(slug))} />
        ) : kind === 'product' ? (
          <AsyncSelect
            id={`${id}-target`}
            value={parsed.kind === 'product' ? parsed.target : ''}
            emptyLabel={t('chooseProduct')}
            fetchPage={productSlugOptions.page}
            fetchOne={productSlugOptions.one}
            onChange={(slug) => onChange(to(slug))}
          />
        ) : kind === 'page' ? (
          <Select id={`${id}-target`} value={parsed.kind === 'page' ? parsed.target : ''} onChange={(event) => onChange(to(event.target.value))}>
            <option value="">{t('choose')}</option>
            {pages.map((page) => (
              <option key={page.slug} value={page.slug}>
                {page.label}
              </option>
            ))}
          </Select>
        ) : kind === 'custom' ? (
          <Input
            id={`${id}-target`}
            dir="ltr"
            placeholder="/products?sort=newest"
            value={parsed.kind === 'custom' ? parsed.target : value}
            onChange={(event) => onChange(event.target.value.trim() || undefined)}
          />
        ) : kind === 'products' ? (
          <Input id={`${id}-target`} dir="ltr" value="/products" disabled />
        ) : null}
      </div>
      {kind === 'custom' ? <p className="text-xs text-ink-muted">{t('linkCustomHint')}</p> : hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

// ─── Categories ──────────────────────────────────────────────────────────────

function CategorySelect({ id, value, onChange }: { id?: string; value: string; onChange: (slug: string) => void }) {
  const t = useT();
  return (
    <AsyncSelect
      id={id}
      value={value}
      emptyLabel={t('chooseCategory')}
      fetchPage={categorySlugOptions.page}
      fetchOne={categorySlugOptions.one}
      onChange={(slug) => onChange(slug)}
    />
  );
}

/** Says so when a stored slug names a category that has since gone. */
function GoneNote({ slug }: { slug: string }) {
  const t = useT();
  const { categoryNames } = useContext(EditorContext);
  if (!slug || !categoryNames || categoryNames.has(slug)) return null;
  return (
    <p className="text-xs text-warning">
      <span dir="ltr">{slug}</span> — {t('categoryGone')}
    </p>
  );
}

function CategoryField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (next: unknown) => void }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <CategorySelect id={id} value={value} onChange={(slug) => onChange(slug || undefined)} />
      <GoneNote slug={value} />
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/** Categories in the order they show: added one at a time, moved and removed in place. */
function CategoriesField({
  label,
  hint,
  max,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  max: number;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const t = useT();
  const id = useId();
  const { categoryNames } = useContext(EditorContext);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {value.length > 0 ? (
        <ol className="flex flex-col divide-y divide-line rounded-control border border-line bg-surface">
          {value.map((slug, index) => {
            const name = categoryNames?.get(slug);
            return (
              <li key={slug} className="flex items-center gap-2 px-3 py-1.5">
                <span data-numeric className="w-5 shrink-0 text-center text-xs text-ink-faint">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{name ?? slug}</span>
                  {categoryNames && !name ? <span className="block text-xs text-warning">{t('categoryGone')}</span> : null}
                </span>
                <IconButton label={t('moveUp')} disabled={index === 0} onClick={() => onChange(moveItem(value, index, index - 1))} className="size-7 border-0 disabled:opacity-35">
                  <ArrowLeftIcon className="size-3.5 rotate-90" />
                </IconButton>
                <IconButton
                  label={t('moveDown')}
                  disabled={index === value.length - 1}
                  onClick={() => onChange(moveItem(value, index, index + 1))}
                  className="size-7 border-0 disabled:opacity-35"
                >
                  <ArrowLeftIcon className="size-3.5 -rotate-90" />
                </IconButton>
                <IconButton label={t('remove')} onClick={() => onChange(value.filter((entry) => entry !== slug))} className="size-7 border-0 hover:text-danger">
                  <TrashIcon className="size-3.5" />
                </IconButton>
              </li>
            );
          })}
        </ol>
      ) : null}
      {value.length < max ? (
        <AsyncSelect
          id={id}
          value=""
          emptyLabel={t('addCategory')}
          allowClear={false}
          fetchPage={async (search, cursor) => {
            const page = await categorySlugOptions.page(search, cursor);
            return { ...page, options: page.options.filter((option) => !value.includes(option.id)) };
          }}
          onChange={(slug) => {
            if (slug && !value.includes(slug)) onChange([...value, slug]);
          }}
        />
      ) : null}
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconField({ label, value, onChange }: { label: string; value: string; onChange: (next: unknown) => void }) {
  const locale = useUiStore((state) => state.locale);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
        {SECTION_ICONS.map((icon) => {
          const selected = icon === value;
          return (
            <button
              key={icon}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={ICON_COPY[icon][locale]}
              title={ICON_COPY[icon][locale]}
              onClick={() => onChange(icon)}
              className={cx(
                'inline-flex size-9 items-center justify-center rounded-control border transition-colors',
                selected ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink',
              )}
            >
              <SectionGlyph icon={icon} className="size-[18px]" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── A section's list: slides, tiles, promises ───────────────────────────────

/**
 * The items a section holds, each a card that folds: one open at a time, the
 * rest a line each, so a slideshow of six reads as six rows and not as six
 * forms. Added, moved, copied and removed in place.
 */
export function ItemsEditor({
  type,
  items,
  value,
  onChange,
  problems,
  resetKey,
}: {
  type: string;
  items: HomeSectionItems;
  value: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  /** The section's problems; each card takes its own. */
  problems: HomeLayoutProblem[];
  /** Changes when another section is opened, so the first card opens again. */
  resetKey: string;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { catalog } = useContext(EditorContext);
  const copy = itemCopy(items, locale);
  const one = copy.one;
  const [open, setOpen] = useState<number | null>(value.length > 0 ? 0 : null);
  useEffect(() => setOpen(value.length > 0 ? 0 : null), [resetKey]);

  const full = value.length >= items.max;

  return (
    <div className="flex flex-col gap-2">
      {value.length === 0 && items.placeholder ? (
        // The page is not empty while the list is: say what it shows instead.
        <p className="rounded-control border border-dashed border-line bg-surface-sunken/60 px-3 py-2 text-xs text-ink-muted">
          {items.placeholder[locale]}
        </p>
      ) : null}
      {value.map((item, index) => {
        const mine = problems.filter((problem) => problem.item === index);
        const expanded = open === index;
        const title = itemTitle(item, locale);
        const thumb = imageSource(item.image);
        return (
          <div key={index} className={cx('rounded-control border bg-surface', expanded ? 'border-brand-300' : mine.length > 0 ? 'border-danger/60' : 'border-line')}>
            <div className="flex items-center gap-1.5 px-2 py-1.5">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : index)}
                className="flex min-w-0 flex-1 items-center gap-2.5 py-0.5 text-start"
              >
                {thumb ? (
                  <img src={thumb} alt="" className="size-9 shrink-0 rounded-[calc(var(--radius-control)-2px)] border border-line object-cover" />
                ) : typeof item.icon === 'string' ? (
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-[calc(var(--radius-control)-2px)] bg-surface-sunken text-ink-muted">
                    <SectionGlyph icon={item.icon} className="size-4" />
                  </span>
                ) : (
                  <span data-numeric className="inline-flex size-9 shrink-0 items-center justify-center rounded-[calc(var(--radius-control)-2px)] bg-surface-sunken text-xs text-ink-faint">
                    {index + 1}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">
                    {one} {count(index + 1, locale)}
                  </span>
                  <span className={cx('block truncate text-xs', mine.length > 0 ? 'text-danger' : title ? 'text-ink-muted' : 'text-ink-faint')}>
                    {mine.length > 0 ? problemMessage({ ...mine[0], item: undefined }, locale, catalog) : (title ?? t('sectionItemEmpty'))}
                  </span>
                </span>
              </button>
              <IconButton label={t('moveUp')} disabled={index === 0} onClick={() => onChange(moveItem(value, index, index - 1))} className="size-8 border-0 disabled:opacity-35">
                <ArrowLeftIcon className="size-4 rotate-90" />
              </IconButton>
              <IconButton
                label={t('moveDown')}
                disabled={index === value.length - 1}
                onClick={() => onChange(moveItem(value, index, index + 1))}
                className="size-8 border-0 disabled:opacity-35"
              >
                <ArrowLeftIcon className="size-4 -rotate-90" />
              </IconButton>
              <IconButton
                label={t('duplicate')}
                disabled={full}
                onClick={() => {
                  const next = [...value];
                  next.splice(index + 1, 0, JSON.parse(JSON.stringify(item)) as Record<string, unknown>);
                  onChange(next);
                  setOpen(index + 1);
                }}
                className="size-8 border-0 disabled:opacity-35"
              >
                <CopyIcon className="size-4" />
              </IconButton>
              <IconButton
                label={t('remove')}
                onClick={() => {
                  onChange(value.filter((_, position) => position !== index));
                  setOpen(null);
                }}
                className="size-8 border-0 hover:text-danger"
              >
                <TrashIcon className="size-4" />
              </IconButton>
              <IconButton label={expanded ? t('done') : t('editContent')} aria-expanded={expanded} onClick={() => setOpen(expanded ? null : index)} className="size-8 border-0">
                <ChevronDownIcon className={cx('size-4 transition-transform', expanded && 'rotate-180')} />
              </IconButton>
            </div>
            {expanded ? (
              <div className="border-t border-line bg-surface-sunken/50 p-4">
                <SectionFields
                  type={type}
                  fields={items.fields}
                  config={item}
                  problems={mine}
                  onChange={(next) => onChange(value.map((entry, position) => (position === index ? next : entry)))}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={full}
          onClick={() => {
            const icon = items.fields.find((field) => field.kind === 'icon');
            onChange([...value, icon?.default !== undefined ? { [icon.name]: icon.default } : {}]);
            setOpen(value.length);
          }}
        >
          <PlusIcon className="size-4" />
          {copy.add}
        </Button>
        <span data-numeric className="text-xs text-ink-faint">
          {full ? t('sectionItemsFull', { max: count(items.max, locale) }) : `${count(value.length, locale)} / ${count(items.max, locale)}`}
        </span>
      </div>
    </div>
  );
}

function itemTitle(item: Record<string, unknown>, locale: 'ar' | 'en'): string | null {
  const title = readBilingual(item.title);
  const other = locale === 'ar' ? 'en' : 'ar';
  return title[locale].trim() || title[other].trim() || null;
}

/** A labelled group inside the editor panel. */
export function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <header>
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {hint ? <p className="mt-0.5 text-xs text-ink-muted">{hint}</p> : null}
      </header>
      {children}
    </section>
  );
}
